<template>
  <div class="__C_Selector">
    <div class="__C_Selector_LabelSection" v-if="label">
      <div class="__C_Selector_LabelSection_Label">
        <div>{{ label }} <slot name="post-label"></slot></div>
      </div>

      <div class="__C_Selector_LabelSection_Description" v-if="description">
        {{ description }}
      </div>
    </div>

    <q-select
      :style="{ width: width, 'min-width': minWidth }"
      :value="value"
      :options="currentOptions"
      :option-value="optionValue"
      :option-label="optionLabel"
      :option-disable="optionDisabled"
      outlined
      dense
      options-dense
      :multiple="multiple"
      :disable="disabled"
      :readonly="readonly"
      :emit-value="emitValue"
      :map-options="mapOptions"
      :use-input="useInput"
      :use-chips="useChips"
      :hide-bottom-space="hideBottomSpace"
      no-error-icon
      :error="!!errorMessage"
      :loading="loading"
      @input="handleInput"
      @filter="filterFn"
      ref="selectRef"
      v-bind="newValueModeComputed"
      @new-value="newValueHandler"
      @filter-abort="abortFilterFn"
    >
      <template v-slot:selected v-if="multiple && !useChips">
        {{ $t('{number} item(s) selected', { number: value.length }) }}
      </template>

      <template v-slot:option="scope" v-if="$scopedSlots.option">
        <slot name="option" :scope="scope" />
      </template>

      <template v-slot:no-option>
        <q-item>
          <q-item-section class="text-grey">
            {{ $t('No results') }}
          </q-item-section>
        </q-item>
      </template>

      <template v-slot:error v-if="errorMessage">
        {{ $t(errorMessage) }}
      </template>
    </q-select>
  </div>
</template>

<script>
/*

    Selector

    emit-value: オプションのvalueの値を利用する。ラベルもvalueの値が表示される。
    例） options: {label: 'ラベル1', value: 'value1'}
         v-modelは `value1`

    map-options: emit-value:trueの場合にlabelを表示させるようにする
   */

export default {
  name: 'CSelector',
  props: {
    value: {
      type: [String, Number, Boolean, Array, Object],
    },
    options: {
      type: Array,
      required: true,
      default: () => {
        return [];
      },
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    optionDisabled: {
      type: String,
      default: 'disabled',
    },
    label: {
      type: [String, Number],
    },
    description: {
      type: String,
    },
    dense: {
      type: Boolean,
      default: false,
    },

    newValueMode: {
      type: String,
      default: '',
    },

    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
    },
    width: {
      type: String,
      default: '100%',
    },
    minWidth: {
      type: String,
    },
    emitValue: {
      type: Boolean,
      default: false,
    },
    mapOptions: {
      type: Boolean,
      default: false,
    },
    useInput: {
      type: Boolean,
      default: true,
    },
    useChips: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
    },

    // TODO: Need to change "errorMessage" to "errorMsg"
    errorMessage: {
      type: String,
    },

    hideBottomSpace: {
      type: Boolean,
    },

    // State
    loading: {
      type: Boolean,
    },
  },
  data() {
    return {
      currentOptions: this.options,
    };
  },
  watch: {
    options(options) {
      this.currentOptions = options;
    },
  },
  mounted() {
    this.currentOptions = this.options;
  },
  computed: {
    newValueModeComputed() {
      return this.newValueMode
        ? {
            newValueMode: this.newValueMode,
          }
        : null;
    },
  },
  methods: {
    handleInput(val) {
      this.$emit('input', val);
      this.$emit('change', val);
    },
    newValueHandler(val) {
      this.$emit('new-value', val);
    },
    filterFn(val, update) {
      // call abort() at any time if you can't retrieve data somehow
      update(
        () => {
          if (val === '') {
            this.currentOptions = this.options;
          } else {
            const needle = val.toLowerCase();
            this.currentOptions = this.options.filter(
              v => v[this.optionLabel].toLowerCase().indexOf(needle) > -1
            );
          }
        },

        // next function is available in Quasar v1.7.4+;
        // "ref" is the Vue reference to the QSelect
        ref => {
          if (val !== '' && ref.options.length > 0) {
            // DO NOT use setOptionIndex or moveOptionSelection with fill-input
            ref.setOptionIndex(-1); // reset optionIndex in case there is something selected
            ref.moveOptionSelection(); // focus the first selectable option
          }
        }
      );
    },
    abortFilterFn() {
      // console.log('delayed filter aborted')
    },
  },
};
</script>

<style lang="scss" scoped>
.__C_Selector {
  position: relative;

  &_LabelSection {
    margin-bottom: $margin-xs;

    &_Label {
      display: flex;
      align-items: center;
    }

    &_Description {
      font-size: $font-size-sm;
      color: rgba($neutral-secondary-color, 0.8);
    }
  }
}
</style>
